<template>
  <!-- 自定义弹窗容器 -->
  <div class="custom-dialog" :class="{ 'dialog-visible': visible }">
    <!-- 遮罩层 -->
    <div class="dialog-mask" @click="handleMaskClick"></div>

    <!-- 弹窗内容 -->
    <div class="dialog-wrapper" :style="{ width: dialogWidth }">
      <!-- 关闭按钮 - 右上角 -->
      <button class="dialog-close-btn" @click="handleCancel">×</button>

      <!-- 弹窗主体 -->
      <div class="avatar-cropper-dialog">
        <div class="main-layout">
          <!-- 左侧：裁剪区域 - 仅通过上传获取图片 -->
          <div class="cropper-section">
            <div id="combined-container" class="cropper-container" @dragover.prevent="handleDragOver"
              @dragleave="handleDragLeave" @drop.prevent="handleDrop" @click="handleContainerClick">

              <!-- 上传提示覆盖层 -->
              <div id="upload-overlay" class="upload-overlay" :class="{ hidden: isImageLoaded }">
                <div class="upload-icon">
                  <img src="./assets/img/upload.png" alt="上传">
                </div>
                <h2 class="upload-text">上传图片</h2>
                <p class="upload-subtext">点击或拖放图片到此处</p>
                <p class="upload-format">支持 JPG, PNG 格式</p>
                <input type="file" id="image-upload" accept="image/*" class="hidden" ref="fileInput"
                  @change="handleFileChange">
              </div>

              <!-- 裁剪相关元素 -->
              <img id="preview-image" :src="previewImageSrc" alt="预览图片" v-if="previewImageSrc" crossOrigin="anonymous">
              <div id="crop-circle" class="crop-circle" :style="{
                width: circleSize + 'px',
                height: circleSize + 'px',
                left: currentLeft + 'px',
                top: currentTop + 'px',
                display: isImageLoaded ? 'block' : 'none'
              }" @mousedown="startDrag" @touchstart.prevent="startDrag">
              </div>
            </div>

            <!-- 裁剪控制按钮 -->
            <div id="crop-controls" class="crop-controls" v-if="isImageLoaded">
              <el-button @click="resetCropPosition" type="default" plain>
                <span>↺</span>重置
              </el-button>
              <el-button @click="zoomIn" type="default" plain>
                <span>➕</span>放大
              </el-button>
              <el-button @click="zoomOut" type="default" plain>
                <span>➖</span>缩小
              </el-button>
            </div>
          </div>

          <!-- 右侧：预览区域 -->
          <div class="preview-section">
            <div class="preview-card">
              <div id="result-container" class="result-container">
                <!-- 初始显示父组件传来的图片 -->
                <img v-if="!showCroppedResult && originalImage" :src="originalImage" alt="原始图片"
                  class="original-image-preview">
                <!-- 裁剪后显示裁剪结果 -->
                <img v-if="showCroppedResult && resultImageSrc" :src="resultImageSrc" alt="裁剪结果" id="result-image">
                <!-- 占位符 -->
                <div class="result-placeholder" v-if="!originalImage && !resultImageSrc">👤</div>
              </div>
              <h3 class="preview-title">
                <span>当前头像</span>
              </h3>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部按钮区域 -->
      <div class="dialog-footer">
        <el-button type="primary" @click="handleConfirm" :disabled="!canConfirm">保存</el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AvatarCropperDialog',
  props: {
    // 控制弹窗显示/隐藏
    visible: {
      type: Boolean,
      default: false
    },
    // 弹窗宽度
    dialogWidth: {
      type: String,
      default: '700px'
    },
    // 父组件传来的原始图片
    originalImage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // 图片相关
      previewImageSrc: '', // 上传的图片源
      resultImageSrc: '',  // 裁剪后的图片
      showCroppedResult: false, // 是否显示裁剪结果

      // 裁剪框相关
      isImageLoaded: false,
      isDragging: false,
      startX: 0,
      startY: 0,
      currentLeft: 0,
      currentTop: 0,
      circleSize: 150,
      maxCircleSize: 0,
      imageRatio: 1,

      // 其他
      debounceTimer: null
    }
  },
  computed: {
    // 确认按钮是否可点击
    canConfirm() {
      return this.resultImageSrc || !!this.originalImage;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        // 显示弹窗时添加防滚动处理
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
          this.calculateMaxCircleSize();
        });
      } else {
        // 隐藏弹窗时恢复滚动
        document.body.style.overflow = '';
        this.resetState();
      }
    },
    // 监听原始图片变化
    originalImage(newVal) {
      if (!this.resultImageSrc) {
        this.showCroppedResult = false;
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleWindowResize);
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('touchmove', this.handleDrag);
    document.removeEventListener('touchend', this.stopDrag);
    // 清理样式
    document.body.style.overflow = '';
  },
  methods: {
    // 点击遮罩层处理
    handleMaskClick() {
      // 点击遮罩不关闭弹窗，保持原有逻辑
    },

    // 重置组件状态
    resetState() {
      this.previewImageSrc = '';
      this.resultImageSrc = '';
      this.showCroppedResult = false;
      this.isImageLoaded = false;
      this.currentLeft = 0;
      this.currentTop = 0;
      this.circleSize = 150;
      this.maxCircleSize = 0;
    },

    // 处理文件上传
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.readFile(file);
      }
      this.$refs.fileInput.value = '';
    },

    // 读取文件并显示
    readFile(file) {
      if (!file.type.match('image.*')) {
        this.$message.error('请上传图片文件');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.loadImageSafely(e.target.result);
      };
      reader.readAsDataURL(file);
    },

    // 安全加载图片
    loadImageSafely(url) {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        this.previewImageSrc = url;
        this.imageRatio = img.width / img.height;
        this.isImageLoaded = true;
        this.calculateMaxCircleSize();
        this.resetCropPosition();
      };

      img.onerror = (error) => {
        console.error('图片加载失败:', error);
        this.$message.error('图片加载失败，请尝试其他图片');
        this.isImageLoaded = false;
      };

      img.src = url;
    },

    // 计算最大裁剪框大小
    calculateMaxCircleSize() {
      const container = document.getElementById('combined-container');
      if (container) {
        this.maxCircleSize = Math.min(container.offsetWidth, container.offsetHeight) * 0.8;
        if (this.circleSize > this.maxCircleSize) {
          this.circleSize = this.maxCircleSize;
        }
      }
    },

    // 重置裁剪框位置
    resetCropPosition() {
      const container = document.getElementById('combined-container');
      if (container) {
        this.currentLeft = (container.offsetWidth - this.circleSize) / 2;
        this.currentTop = (container.offsetHeight - this.circleSize) / 2;
        this.applyCrop();
      }
    },

    // 开始拖动
    startDrag(e) {
      e.stopPropagation();
      this.isDragging = true;

      if (e.type === 'mousedown') {
        this.startX = e.clientX;
        this.startY = e.clientY;
      } else {
        this.startX = e.touches[0].clientX;
        this.startY = e.touches[0].clientY;
      }

      const cropCircle = document.getElementById('crop-circle');
      cropCircle.classList.add('scale-105');

      document.addEventListener('mousemove', this.handleDrag);
      document.addEventListener('touchmove', this.handleDrag, { passive: false });
      document.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('touchend', this.stopDrag);
    },

    // 拖动处理
    handleDrag(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      e.stopPropagation();

      let clientX, clientY;
      if (e.type === 'mousemove') {
        clientX = e.clientX;
        clientY = e.clientY;
      } else {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      }

      const dx = clientX - this.startX;
      const dy = clientY - this.startY;

      this.startX = clientX;
      this.startY = clientY;

      const container = document.getElementById('combined-container');

      this.currentLeft += dx;
      this.currentTop += dy;

      this.currentLeft = Math.max(0, Math.min(this.currentLeft, container.offsetWidth - this.circleSize));
      this.currentTop = Math.max(0, Math.min(this.currentTop, container.offsetHeight - this.circleSize));

      this.debounceApplyCrop();
    },

    // 停止拖动
    stopDrag(e) {
      e.stopPropagation();
      this.isDragging = false;

      const cropCircle = document.getElementById('crop-circle');
      cropCircle.classList.remove('scale-105');

      document.removeEventListener('mousemove', this.handleDrag);
      document.removeEventListener('touchmove', this.handleDrag);
      document.removeEventListener('mouseup', this.stopDrag);
      document.removeEventListener('touchend', this.stopDrag);

      this.applyCrop();
    },

    // 放大裁剪框
    zoomIn() {
      if (this.circleSize < this.maxCircleSize) {
        this.circleSize += 20;
        if (this.circleSize > this.maxCircleSize) {
          this.circleSize = this.maxCircleSize;
        }
        this.adjustPositionAfterResize();
        this.applyCrop();
      }
    },

    // 缩小裁剪框
    zoomOut() {
      if (this.circleSize > 50) {
        this.circleSize -= 20;
        this.adjustPositionAfterResize();
        this.applyCrop();
      }
    },

    // 调整大小后确保裁剪框在容器内
    adjustPositionAfterResize() {
      const container = document.getElementById('combined-container');

      if (this.currentLeft + this.circleSize > container.offsetWidth) {
        this.currentLeft = container.offsetWidth - this.circleSize;
      }

      if (this.currentTop + this.circleSize > container.offsetHeight) {
        this.currentTop = container.offsetHeight - this.circleSize;
      }
    },

    // 应用裁剪
    applyCrop() {
      if (!this.previewImageSrc) return;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = this.circleSize;
      canvas.height = this.circleSize;

      const cropX = this.currentLeft;
      const cropY = this.currentTop;
      const cropSize = this.circleSize;

      const imgElement = document.getElementById('preview-image');
      if (!imgElement) return;

      const imgRect = imgElement.getBoundingClientRect();
      const imgScaleX = imgElement.naturalWidth / imgRect.width;
      const imgScaleY = imgElement.naturalHeight / imgRect.height;

      const actualX = cropX * imgScaleX;
      const actualY = cropY * imgScaleY;
      const actualSize = cropSize * Math.max(imgScaleX, imgScaleY);

      // 创建圆形裁剪区域
      ctx.beginPath();
      ctx.arc(cropSize / 2, cropSize / 2, cropSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      // 绘制裁剪后的图像
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          ctx.drawImage(
            img,
            actualX, actualY,
            actualSize, actualSize,
            0, 0,
            cropSize, cropSize
          );

          try {
            this.resultImageSrc = canvas.toDataURL('image/png');
            this.showCroppedResult = true;
          } catch (error) {
            console.error('导出图片失败:', error);
            this.$message.error('图片处理失败，可能是由于跨域限制');
          }
        } catch (error) {
          console.error('绘制图片失败:', error);
          this.$message.error('图片处理失败，请尝试其他图片');
        }
      };

      img.onerror = (error) => {
        console.error('裁剪时加载图片失败:', error);
        this.$message.error('图片处理失败，请尝试其他图片');
      };

      img.src = this.previewImageSrc;
    },

    // 防抖函数
    debounceApplyCrop() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.applyCrop();
      }, 50);
    },

    // 处理窗口大小变化
    handleWindowResize() {
      if (this.isImageLoaded) {
        this.calculateMaxCircleSize();
        this.adjustPositionAfterResize();
        this.applyCrop();
      }
    },

    // 处理拖放相关事件
    handleDragOver() {
      const uploadOverlay = document.getElementById('upload-overlay');
      uploadOverlay.style.border = '2px dashed #409EFF';
      uploadOverlay.style.backgroundColor = '#eff6ff';
    },

    handleDragLeave() {
      const uploadOverlay = document.getElementById('upload-overlay');
      uploadOverlay.style.border = 'none';
      uploadOverlay.style.backgroundColor = 'var(--white)';
    },

    handleDrop(e) {
      const uploadOverlay = document.getElementById('upload-overlay');
      uploadOverlay.style.border = 'none';
      uploadOverlay.style.backgroundColor = 'var(--white)';

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        this.readFile(e.dataTransfer.files[0]);
      }
    },

    // 处理容器点击事件
    handleContainerClick() {
      if (!this.isImageLoaded) {
        this.$refs.fileInput.click();
      }
    },

    // 取消按钮
    handleCancel() {
      this.$emit('update:visible', false);
      this.$emit('cancel');
      this.$emit('close');
    },

    // 确认按钮
    handleConfirm() {
      console.log(this.resultImageSrc, this.originalImage, 'resultImageSrc')
      if (this.resultImageSrc) {
        console.log(this.resultImageSrc, 'resultImageSrc')
        this.$emit('confirm', this.resultImageSrc);
      } else if (this.originalImage) {
        this.$emit('confirm', this.originalImage);
      }
      this.$emit('update:visible', false);
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
:root {
  --primary: #3B82F6;
  --secondary: #10B981;
  --neutral: #6B7280;
  --dark: #1F2937;
  --light-gray: #f3f4f6;
  --border-gray: #d1d5db;
  --white: #ffffff;
}

.custom-dialog {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  height: 100%;
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
}

.custom-dialog.dialog-visible {
  opacity: 1;
  visibility: visible;
}

/* 遮罩层 */
.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1001;
  width: 100%;
  height: 100%;
  transition: background-color 0.3s;
}


.dialog-wrapper {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 1002;
  transform: translate(-50%, -50%) scale(0.95);
  transition: transform 0.3s ease-out;
  border-radius: 8px;
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.2);
  background: linear-gradient(180deg, rgba(177, 110, 249, 0.1) 0%, rgba(126, 180, 235, 0.1) 100%);
  /*   backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px); */
  padding: 20px;
  position: relative;
  backdrop-filter: blur(55.70000076293945px);
  min-height: 200px;
  max-height: 400px;
}

.custom-dialog.dialog-visible .dialog-wrapper {
  transform: translate(-50%, -50%) scale(1);
}

/* 关闭按钮 - 右上角 */
.dialog-close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--neutral);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
}

.dialog-close-btn:hover {
  background-color: var(--light-gray);
  color: var(--dark);
}

/* 底部按钮区域 */
.dialog-footer {
  padding: 16px;
  text-align: center;
  margin-bottom: 10px;
}
.dialog-footer button{
background: linear-gradient(180deg, rgba(177, 110, 249, 0.6) 0%, rgba(126, 180, 235, 0.6) 100%);
border-radius: 35px;
backdrop-filter: blur(30px);
box-shadow: 4px 4px 4px 0px #00000026;
width: 80px;

}
.avatar-cropper-dialog {
  padding: 10px 0;
}

.main-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

@media (min-width: 768px) {
  .main-layout {
    flex-direction: row;
    align-items: flex-start;
  }

  .cropper-section,
  .preview-section {
    width: 50%;
    padding: 0 10px;
  }
}

.cropper-container {
  position: relative;
  overflow: hidden;
  border: 2px solid var(--border-gray);
  border-radius: 8px;
  background-color: var(--light-gray);
  aspect-ratio: 1 / 1;
  max-width: 400px;
  max-height: 260px;
  margin: 0 auto;
}

.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #EDEDED99;
  z-index: 20;
  transition: opacity 0.3s ease, background-color 0.3s, border 0.3s;
}

.upload-overlay.hidden {
  opacity: 0;
  pointer-events: none;
}

.upload-icon {
  font-size: 60px;
  color: #9ca3af;
  margin-bottom: 16px;
}

.upload-icon>img {
  width: 90px;

}

.upload-text {
  font-size: 18px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 8px;
}

.upload-subtext {
  color: #6b7280;
  margin-bottom: 16px;
  font-size: 14px;
}

.upload-format {
  font-size: 12px;
  color: #9ca3af;
}

#preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  z-index: 1;
}

.crop-circle {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  cursor: move;
  pointer-events: auto;
  z-index: 10;
}

.crop-circle.scale-105 {
  transform: scale(1.05);
  transition: transform 0.2s ease;
}

.crop-controls {
  margin-top: 16px;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.preview-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-card {
  background-color: var(--white);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  width: 100%;
  max-width: 250px;
}

.preview-title {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-title span {
  font-family: Manrope;
  font-weight: 500;
  font-style: Medium;
  font-size: 22px;
  letter-spacing: 0%;
  margin-right: 8px;
  color: #767373CC;

}

.result-container {
  border: 4px solid var(--light-gray);
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 200px;
  height: 200px;
  margin: 0 auto 16px;
  background-color: var(--light-gray);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 原始图片预览样式 */
.original-image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.result-placeholder {
  font-size: 72px;
  color: #d1d5db;
}

#result-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

#image-upload {
  display: none;
}
</style>
